import {
    ButtonFixedFooterLayout,
    ButtonPrimary,
    Callout,
    IconChevronLeftRegular,
    IconWarningRegular,
    NavigationBar,
    Stack,
    Text2,
    Text3,
    Text4,
    ThemeVariant,
    skinVars,
    useDialog,
} from '@telefonica/mistica';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { cancelDatasharing, hireDatasharing } from '../../../services/ProductService';
import navigateParams from '../../../utils/navigation/navigateParams';
import './index.scss';
import { useTranslation } from 'react-i18next';
import { AxiosError, AxiosResponse } from 'axios';
import { TrackingEvents } from '../../../utils/dataTransformation/tracking';
import { isWebviewAndroid, isWebviewIOS } from '../../../utils/navigation/controlDevices';
import Stopper from '../../../components/Stopper';
import { managementEnvironment } from '../../../utils/dataTransformation/environments';
import BreadCrumbsWrapper from '../../../components/BreadcrumbsWrapper';

const EnableSharedData: React.FC = () => {
    const homeDisabled = managementEnvironment().REACT_APP_STOPPER_HOME_DISABLED;
    const { t } = useTranslation();
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const { trackingScreenView } = new TrackingEvents();
    const { trackingUserInteraction } = new TrackingEvents();
    const [activationInProcess, setActivationProcess] = useState<boolean>();
    const enable = params.get('status');
    const { dialog } = useDialog();

    let storage: string;
    if (window.sessionStorage.getItem('storage')) {
        storage = window.sessionStorage.getItem('storage')!;
    }

    const handleErrorHttp = ({ id, title, localizedDescription }: any) => {
        dialog({
            icon: <IconWarningRegular color={skinVars.colors.feedbackErrorBackground} size={64} />,
            title: t('dialogTitleSharedData'),

            message: (() => {
                return (
                    <Stack space={16}>
                        <Text3 light color={skinVars.colors.textSecondary}>
                            {localizedDescription}
                        </Text3>
                        <Text3 light color={skinVars.colors.textSecondary}>
                            {`${t('codeError')} ${id}`}
                        </Text3>
                    </Stack>
                );
            })() as any,
            acceptText: t('buttonUnderstoodFeedback'),
            onAccept: () => {},
            onCancel: () => {},
        });
    };

    const pushButtonDataShared = () => {
        if (enable === 'false') {
            hireDatasharing()
                .then((response: AxiosResponse) => {
                    if (response) {
                        sendToNative(true, true);
                    } else {
                        return Promise.reject(response);
                    }
                })
                .catch((error: AxiosError) => {
                    const { id, title, localizedDescription } = JSON.parse(sessionStorage.getItem('error')!);
                    const isActiveSameCycle =
                        localizedDescription !==
                        'Has realizado una activación durante este ciclo. Debes esperar al siguiente para volver a activar este servicio.';
                    if (isActiveSameCycle) {
                        sendToNative(false, true, id!);
                    } else {
                        handleErrorHttp({ id, title, localizedDescription });
                    }
                });
        } else {
            cancelDatasharing()
                .then((response: AxiosResponse) => {
                    if (response) {
                        sendToNative(true, false);
                    } else {
                        return Promise.reject();
                    }
                })
                .catch((error: AxiosError) => {
                    console.error('error', error);
                    const { id, title, localizedDescription } = JSON.parse(sessionStorage.getItem('error')!);
                    const isActiveSameCycle =
                        localizedDescription !==
                        'Has realizado una activación durante este ciclo. Debes esperar al siguiente para volver a activar este servicio.';
                    if (isActiveSameCycle) {
                        sendToNative(false, false, id!);
                    } else {
                        handleErrorHttp({ id, title, localizedDescription });
                    }
                });
        }
    };

    const sendToNative = (success: boolean, active: boolean, error?: string) => {
        const type = success ? 'feedback-success' : 'feedback-error';
        const url = `/enable-shared-data/enable-shared-data-message`;
        const isSuccess = success ? 'success' : 'error';
        const isEnable = active ? 'enable' : 'disabled';
        const literals = {
            success: {
                enable: {
                    title: t('enableSharedDataInfoFeedbackTitle'),
                    subtitle: t('enableSharedDataInfoFeedbackDescriptionActivate'),
                },
                disabled: {
                    title: t('enableSharedDataInfoFeedbackTitle'),
                    subtitle: t('enableSharedDataInfoFeedbackDescriptionDeactivate'),
                },
            },
            error: {
                enable: {
                    title: t('enableSharedDataErrorFeedbackTitleActivate'),
                    subtitle: t('enableSharedDataErrorFeedbackDescriptionActivate'),
                },
                disabled: {
                    title: t('enableSharedDataErrorFeedbackTitleDeactivate'),
                    subtitle: t('enableSharedDataErrorFeedbackDescriptionDeactivate'),
                },
            },
        };
        const queryParams = {
            state: success ? 'SUCCESS' : 'ERROR',
            title: literals[isSuccess][isEnable].title,
            subtitle: literals[isSuccess][isEnable].subtitle,
            errorCode: error ? `${t('feedbackCodeError')} ${error}` : '',
        };

        if (navigateParams(type, url, queryParams, {})) {
            navigate(url, { state: { success, enable, error } });
        }
    };

    let refTimeout = useRef<number>(0);

    useEffect(() => {
        if (!(isWebviewAndroid || isWebviewIOS)) {
            trackingScreenView();
        }

        () => {
            window.clearTimeout(refTimeout.current);
        };
    }, []);

    return homeDisabled ? (
        <Stopper></Stopper>
    ) : (
        <div className='enableSharedData-page'>
            <div
                className='enableSharedData-page__header'
                style={{
                    background: skinVars.colors.backgroundBrand,
                }}
            >
                {!(isWebviewAndroid || isWebviewIOS) && (
                    <>
                        <div className='enableSharedData-page__header-navigationBar'>
                            <NavigationBar
                                isInverse={true}
                                title={t('sharedData')}
                                onBack={() => {
                                    navigate(-1);
                                }}
                            />
                        </div>
                        <div className='enableSharedData-page__header-return'>
                            <div
                                className='enableSharedData-page__header-return-cursor'
                                onClick={() => {
                                    navigate(-1);
                                }}
                            >
                                <div className='enableSharedData-page__header-return-icon'>
                                    <IconChevronLeftRegular color={skinVars.colors.appBarBackground} size={20} />
                                </div>
                                <Text2 medium color={skinVars.colors.appBarBackground}>
                                    {t('returnResponsive')}
                                </Text2>
                            </div>
                        </div>
                    </>
                )}
                <div className='enableSharedData-page__header-breadcrumbs'>
                    <ThemeVariant isInverse>
                        <BreadCrumbsWrapper
                            title={t('sharedData')}
                            breadcrumbs={[{ title: t('breadcrumbsHome'), url: '/home' }]}
                        />
                    </ThemeVariant>
                </div>
            </div>
            <ButtonFixedFooterLayout
                button={
                    <ButtonPrimary
                        onPress={() => {
                            trackingUserInteraction('shared_data_button_tapped');
                            pushButtonDataShared();
                        }}
                    >
                        {enable === 'true' ? t('enableShareBtnDeactivate') : t('enableShareBtnActivate')}
                    </ButtonPrimary>
                }
            >
                <div className='enableSharedData-page__content'>
                    <div className='enableSharedData-page__content-title'>
                        <Text4 regular>{t('enableShareDataOneTitle')}</Text4>
                    </div>
                    <div className='enableSharedData-page__content-definition'>
                        <Text3 regular color={skinVars.colors.textSecondary}>
                            {t('enableShareDataOneDescription')}
                        </Text3>
                    </div>
                    <div className='enableSharedData-page__content-subtitle'>
                        <Text4 regular>{t('enableShareDataTwoTitle')}</Text4>
                    </div>
                    <div className='enableSharedData-page__content-when'>
                        <Text3 regular color={skinVars.colors.textSecondary}>
                            {t('enableShareDataTwoDescription')}
                        </Text3>
                    </div>

                    {activationInProcess && (
                        <div className='enableSharedData-page__content-callout'>
                            <Callout
                                icon={<IconWarningRegular color={skinVars.colors.warning} />}
                                title={''}
                                description={
                                    'Has realizado una activación durante este ciclo. Debes esperar al siguiente para volver a activar este servicio.'
                                }
                            />
                        </div>
                    )}
                </div>
            </ButtonFixedFooterLayout>
        </div>
    );
};

export default EnableSharedData;
